/**
 * Trims custom symbols from the beginning and the end of string
 * e.g. '.,Dumbo.;', '.;' → 'Dumbo'
 */
const trimChars = function (string: string, symbols: string): string {
  const regexp = new RegExp(`^[${symbols}]+|[${symbols}]+$`, 'g')

  return string.replace(regexp, '')
}

/**
 * Trims html from string
 * e.g. <span>Fu</span>nk → Funk
 */
const trimHtml = (string: string): string => string.replace(/<(?:.|\n)*?>/gm, '')

/**
 * Capitalizes, transforms first letter to uppercase, string
 * e.g. dumbo → Dumbo
 */
const capitalize = (string: string): string => string.charAt(0).toUpperCase() + string.slice(1)

/**
 * Decapitalizes, transforms first letter to lowercase, string
 * e.g. ScentType -> scentType
 */
const decapitalize = (string: string): string => (
  string.split('').map((l, i) => i === 0 ? l.toLowerCase() : l).join('')
)

/**
 * Transforms string to kebab case
 * e.g. 'Kat Von D' → 'kat-von-d'
 */
const kebabCaseify = (string: string): string => string.replace(/\s/g, '-').toLowerCase()

/**
 * Transforms multiline string to oneliner and trims it
 * e.g. '  this\n is the text    ' → 'this is the text'
 */
const multiLineToOneLine = (string: string): string => (
  string
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join(' ')
)

/**
 * Transforms from kebab case to capitalized case
 * e.g. 'kat-von-d' → 'Kat Von D'
 */
const kebabToCapitalizedCase = (string: string): string => (
  string.split('-').map((part) => capitalize(part)).join(' ')
)

/**
 * Transforms from camelCase to capitalized case
 * e.g. 'creditCard' → 'Credit Card'
 */
const camelCaseToCapitalizedCase = (string: string): string => (
  string
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
)

/**
 * Transforms text with additional padding (spaces) to make multiple lines similar.
 *
 * e.g.:
 * - 'foo', 12 → '         foo'
 * - 'foofoofoo', 5 → 'foofoofoo'
 * - 'foo', 12, '.' → '.........foo'
 * - 'foo', 12, null, 'mid' → '     foo    '
 * - 'foo', 12, null, 'start' → 'foo         '
 */
const pad = (
  value: (string | number), width: number, replace: string, position: ('start' | 'mid' | 'end')
): string => {
  const z = replace || ' '
  const n = `${value}`

  if (n.length >= width) {
    return n
  }

  const fill = new Array(width - n.length + 1).join(z)

  if (position === 'start') {
    return n + fill
  }

  if (position === 'mid') {
    const slice = Math.ceil(fill.length / 2)

    return fill.substr(0, slice) + n + fill.substr(slice)
  }

  return fill + n
}

/**
 * Escapes symbals like qoutes and new line in string
 *
 * e.g.:
 * - '"foo"' → '\\"foo\\"'
 * - `foo\n bar` → 'foo\\n bar'
 * - `foo\r bar` → 'foo\\n bar'
 */
const escape = (string: string): string => {
  if (typeof string !== 'string') {
    return string
  }

  return string
    .replace(/"/g, '\\"')
    .replace(/[\n\r]/g, '\\n')
}

export default {
  trimChars,
  trimHtml,
  capitalize,
  decapitalize,
  kebabCaseify,
  multiLineToOneLine,
  kebabToCapitalizedCase,
  camelCaseToCapitalizedCase,
  pad,
  escape,
}
